<template>
  <div class="bzc-root">
    <!-- CHAT-23/a: phones get a floating button instead of the top-bar one (CSS shows it at 600px and below). -->
    <button
      v-if="fab.show && !embedded"
      type="button"
      class="bzc-fab"
      :aria-label="fab.label"
      :data-locked="fab.locked ? 'true' : null"
      data-test="ask-bizminer-fab"
      @click="openFromFab"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z"></path></svg>
      <span class="bzc-fab-text">Ask Bizminer</span>
      <svg v-if="fab.locked" class="bzc-fab-lock" data-test="ask-bizminer-fab-lock" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"></path></svg>
      <span class="bzc-fab-beta">BETA</span>
    </button>

    <section
      v-if="isOpen"
      class="bzc-panel"
      :class="{ 'is-kb': !!kbBox, 'is-embedded': embedded }"
      :style="panelStyle"
      :role="embedded ? 'region' : 'complementary'"
      :aria-label="embedded ? 'Conversation' : 'Ask Bizminer'"
      data-test="ask-bizminer-panel"
    >
      <div v-if="!embedded" class="bzc-head">
        <img class="bzc-head-logo" src="/img/logo-symbol-blue.png" alt="" />
        <div class="bzc-head-title">Ask Bizminer</div>
        <!-- CHAT-08: this conversation is shared with Bizminer support. -->
        <span v-if="shared && mode === 'full'" class="bzc-shared" data-test="ask-bizminer-shared-badge" title="Bizminer support can read this conversation">Shared</span>
        <!-- CHAT-08: the conversation menu; its item opens the share confirm step. -->
        <chat-share-menu
          v-if="mode === 'full' && chat.conversationId"
          variant="menu"
          :conversation-id="chat.conversationId"
          :shared="shared"
          :share-note="shareNote"
          @shared="onShared"
          @unshared="onUnshared"
          @missing="onShareMissing"
          @access="onShareAccess"
        />
        <button
          v-if="mode === 'full'"
          type="button"
          class="bzc-icon-btn"
          aria-label="New conversation"
          title="New conversation"
          :disabled="turnBusy"
          @click="newConversation"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path></svg>
        </button>
        <button
          type="button"
          class="bzc-icon-btn"
          aria-label="Close Ask Bizminer"
          title="Close"
          @click="close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>
        </button>
      </div>

      <div v-if="context && mode === 'full'" class="bzc-context" data-test="ask-bizminer-context">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="#003666" class="bzc-context-icon"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"></path></svg>
        <div class="bzc-context-text">
          <b>Asking about this profile:</b> {{ context.label }}
        </div>
        <button
          v-if="canDropContext"
          type="button"
          class="bzc-context-x"
          aria-label="Stop asking about this profile"
          @click="dropContext"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path></svg>
        </button>
      </div>

      <!-- CHAT-18/e: retail, academic and lapsed accounts see a locked teaser, no composer. -->
      <div v-if="mode === 'teaser'" class="bzc-teaser" data-test="ask-bizminer-teaser">
        <!-- P2: until the server has confirmed the teaser, no lock and no pitch. -->
        <div v-if="teaserView === 'checking'" class="bzc-status" role="status" data-test="ask-bizminer-teaser-checking">
          <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
          Checking your access&hellip;
        </div>
        <div v-else-if="teaserView === 'error'" class="bzc-error" role="alert" data-test="ask-bizminer-connect-error">
          <span>{{ connectError || "Couldn't check your access. Please try again." }}</span>
          <button type="button" class="bzc-link-btn" @click="connect">Retry</button>
        </div>
        <template v-else>
        <span class="bzc-teaser-lock" aria-hidden="true">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"></path></svg>
        </span>
        <template v-if="teaserView === 'retry'">
          <p class="bzc-teaser-retry" data-test="ask-bizminer-teaser-retry">
            Couldn't check your subscription.
            <span v-if="connection === 'connecting'" class="bzc-status">
              <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
              Checking&hellip;
            </span>
            <button v-else type="button" class="bzc-link-btn" @click="connect">Try again</button>
          </p>
        </template>
        <template v-else>
          <div class="bzc-teaser-title">Ask Bizminer is included with a Bizminer subscription.</div>
          <p>
            Ask questions in plain English about any industry, market or company. Ask Bizminer answers from Bizminer data,
            starts with the profiles and reports you already own, and shows where every number comes from.
          </p>
          <div class="bzc-teaser-actions">
            <a
              class="bzc-btn bzc-btn-primary"
              :href="upgradeUrl"
              target="_blank"
              rel="noopener noreferrer"
              data-test="ask-bizminer-subscribe"
              @click="trackTeaser('subscribe')"
            >Subscribe</a>
            <a
              class="bzc-btn bzc-btn-outline"
              :href="contactUrl"
              target="_blank"
              rel="noopener noreferrer"
              data-test="ask-bizminer-contact"
              @click="trackTeaser('contact')"
            >Contact sales</a>
          </div>
        </template>
        </template>
      </div>

      <template v-else>
      <div ref="msgs" class="bzc-msgs" aria-live="polite" @scroll="onScroll">
        <div v-if="notice" class="bzc-notice" role="status" data-test="ask-bizminer-notice">{{ notice }}</div>

        <div v-if="connection === 'connecting'" class="bzc-status">
          <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
          Connecting&hellip;
        </div>

        <div v-if="connection === 'failed'" class="bzc-error" role="alert" data-test="ask-bizminer-connect-error">
          <span>{{ connectError }}</span>
          <button type="button" class="bzc-link-btn" @click="connect">Retry</button>
        </div>

        <div v-if="loadingConv" class="bzc-status" role="status" data-test="ask-bizminer-loading-conversation">
          <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
          Opening the conversation&hellip;
        </div>

        <div v-if="connection === 'ready' && !chat.messages.length && !loadingConv" class="bzc-first">
          <div class="bzc-first-title">What would you like to know?</div>
          <p>
            {{ context ? 'Ask about this profile, any industry, market or company, or the profiles and reports you already own.' : 'Ask about any industry, market or company, or the profiles and reports you already own.' }}
            Looking things up is always free, and nothing is bought without your OK.
          </p>
          <div class="bzc-first-label">Try</div>
          <div class="bzc-starters">
            <button
              v-for="s in starters"
              :key="s"
              type="button"
              class="bzc-chip bzc-chip-block"
              @click="send(s)"
            >{{ s }}</button>
          </div>
        </div>

        <div v-for="(m, idx) in chat.messages" :key="m.id" class="bzc-msg">
          <div v-if="m.role === 'user'" class="bzc-user">{{ m.text }}</div>

          <template v-else>
            <div v-if="m.steps.length" class="bzc-steps" data-test="ask-bizminer-steps">
              <div v-for="st in m.steps" :key="st.id" class="bzc-step" :class="'is-' + st.state">
                <svg v-if="st.state === 'done'" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="#2D6731"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg>
                <svg v-else class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
                <span>{{ st.label }}</span>
              </div>
            </div>

            <div v-if="m.status === 'streaming' && !m.text && !m.steps.length" class="bzc-status bzc-thinking">
              <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
              Thinking&hellip;
            </div>

            <div v-if="m.status === 'answering'" class="bzc-status bzc-thinking" data-test="ask-bizminer-answering">
              <svg class="bzc-spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
              Still answering&hellip;
            </div>

            <div v-if="hasBody(m)" class="bzc-assistant">
              <span class="bzc-avatar" aria-hidden="true"><img src="/img/logo-symbol-blue.png" alt="" /></span>
              <div class="bzc-body">
                <span v-if="m.ownedFree" class="bzc-owned" data-test="ask-bizminer-owned">
                  <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" fill="#2D6731"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg>
                  From what you own &middot; no charge
                </span>

                <!-- Text is escaped by renderMarkdownLite before any markup is added. -->
                <div v-if="m.text" class="bzc-md" v-html="render(m.text)"></div>

                <!-- CHAT-19: charts under the text. Every value is drawn as text only (see ChatChart). -->
                <chat-chart v-for="ch in m.charts" :key="ch.id" :chart="ch" :citations="m.citations" />

                <!-- PFD-235: the Prospect List preview (the free count, or the rows of an owned list). No price; the buy link goes to the app. -->
                <chat-prospects-card v-for="pr in m.prospects" :key="pr.key" :prospects="pr" @open="openLink" />

                <!-- CHAT-11/a: the financial-data notice comes before the figures form; the form stays shut until it is acknowledged. -->
                <chat-notice-card
                  v-if="m.notice"
                  :notice="m.notice"
                  :standard-path="standardPath(m)"
                  :has-form="m.forms.length > 0 || m.confirms.length > 0"
                  :busy="busy"
                  @continue="ackNotice"
                  @standard="openStandard"
                  @reload="reloadNotice"
                />
                <!-- CHAT-11/b, CHAT-22: the input form; what is typed stays on this device (sessionStorage) until the tab closes. -->
                <template v-for="f in m.forms">
                  <chat-form-card v-if="!f.noticeRequired" :key="f.id" :form="f" :uid="uid" :busy="busy" @submit="submitFormCard" />
                </template>

                <div v-for="c in m.citations" :key="c._key" class="bzc-cite" data-test="ask-bizminer-citation">
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="#0a8fc0" class="bzc-cite-icon"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"></path></svg>
                  <span class="bzc-cite-text"><b>Source:</b> {{ cite(c) }}</span>
                  <a v-if="link(c)" class="bzc-cite-open" :href="link(c).href" :target="link(c).external ? '_blank' : null" :rel="link(c).external ? 'noopener noreferrer' : null" data-test="ask-bizminer-citation-open" @click="openCitation($event, c)">{{ openLabel(c) }}</a>
                </div>

                <!-- CHAT-11 (Lender demo): a one-page, printable summary of a Financial Report answer, built by the service from the report. -->
                <button
                  v-if="summaryOf(m)"
                  type="button"
                  class="bzc-btn bzc-btn-outline bzc-summary-btn"
                  :disabled="summaryOf(m) === 'wait' || !!(summary && summary.phase === 'loading')"
                  data-test="ask-bizminer-summary"
                  @click="openSummary(m)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"></path></svg>
                  Create a one-page client summary
                </button>

                <div v-for="c in m.confirms" :key="c.pendingId" class="bzc-confirm" :class="['is-' + c.status, { 'is-action': isAction(c) }]" data-test="ask-bizminer-confirm">
                  <div class="bzc-confirm-head">
                    <svg v-if="isAction(c)" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                    <svg v-else width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"></path></svg>
                    <span>{{ confirmHeadline(c) }}</span>
                  </div>
                  <div class="bzc-confirm-body">
                    <div v-if="!isAction(c)" class="bzc-confirm-product">{{ c.product }}</div>
                    <dl v-if="!isAction(c)" class="bzc-confirm-dl">
                      <template v-if="c.company"><dt>Company</dt><dd>{{ c.company }}</dd></template>
                      <template v-if="c.industry"><dt>Industry</dt><dd>{{ c.industry }}</dd></template>
                      <template v-if="c.area"><dt>Area</dt><dd>{{ c.area }}</dd></template>
                      <template v-if="c.salesClass"><dt>Sales class</dt><dd>{{ c.salesClass }}</dd></template>
                    </dl>

                    <template v-if="c.status === 'pending' && isAction(c)">
                      <!-- A My Data / Company Report action (chatCore CARD_ACTIONS): the prompt is the whole text. No charge line, no purchase copy. -->
                      <p class="bzc-confirm-charge" data-test="ask-bizminer-action-prompt">{{ c.prompt }}</p>
                      <p v-if="c.error" class="bzc-confirm-error" role="alert" data-test="ask-bizminer-confirm-error">{{ c.error }}</p>
                      <div class="bzc-confirm-actions">
                        <button type="button" class="bzc-btn" :class="actionCopy(c).destructive ? 'bzc-btn-danger' : 'bzc-btn-primary'" :disabled="confirmOff(c)" data-test="ask-bizminer-action-confirm" @click="answer(c, 'confirm')">{{ c.error ? 'Try again' : actionCopy(c).confirm }}</button>
                        <button type="button" class="bzc-btn bzc-btn-ghost" :disabled="busy" data-test="ask-bizminer-action-cancel" @click="answer(c, 'decline')">{{ actionCopy(c).cancel }}</button>
                      </div>
                      <div class="bzc-fine">{{ expiryNote(c) }}</div>
                    </template>
                    <template v-else-if="c.status === 'pending'">
                      <p class="bzc-confirm-charge" data-test="ask-bizminer-charge">{{ chargeLine(c) }}</p>
                      <p class="bzc-confirm-note">
                        You'll own it from then on, and opening it again here, in the app or through the API is free.
                      </p>
                      <p v-if="subNote(c)" class="bzc-subuser" data-test="ask-bizminer-subuser">{{ subNote(c) }}</p>
                      <p v-if="c.error" class="bzc-confirm-error" role="alert" data-test="ask-bizminer-confirm-error">{{ c.error }}</p>
                      <div class="bzc-confirm-actions">
                        <button type="button" class="bzc-btn bzc-btn-primary" :disabled="confirmOff(c)" @click="answer(c, 'confirm')">{{ c.error ? 'Try again' : 'Confirm purchase' }}</button>
                        <button type="button" class="bzc-btn bzc-btn-ghost" :disabled="busy" @click="answer(c, 'decline')">Not now</button>
                      </div>
                      <div class="bzc-fine">{{ expiryNote(c) }}</div>
                    </template>
                    <div v-else-if="c.status === 'buying'" class="bzc-status">
                      <svg class="bzc-spin" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
                      {{ isAction(c) ? actionCopy(c).busy : 'Completing your purchase\u2026' }}
                    </div>
                    <div v-else-if="c.status === 'declining'" class="bzc-status">Cancelling&hellip;</div>
                    <div v-else-if="c.status === 'owned' || c.status === 'already_owned' || c.status === 'deleted' || c.status === 'saved'" class="bzc-confirm-done">
                      <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="#2D6731"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"></path></svg>
                      {{ c.message || (isAction(c) ? actionCopy(c).done : 'Added to your profiles and reports.') }}
                    </div>
                    <div v-else-if="c.status === 'declined'" class="bzc-fine bzc-fine-dark">{{ c.message }}</div>
                    <div v-else-if="c.status === 'stalled'" class="bzc-stalled" data-test="ask-bizminer-stalled">
                      <p class="bzc-confirm-note">{{ c.message }}</p>
                      <button type="button" class="bzc-btn bzc-btn-outline" :disabled="busy || rechecking" @click="recheck">{{ rechecking ? 'Checking\u2026' : 'Check again' }}</button>
                    </div>
                    <template v-else>
                      <!-- expired | rejected | not_covered | redeeming: the text is the server's, or says only what the server's state proves -->
                      <div v-if="c.status === 'redeeming'" class="bzc-status" data-test="ask-bizminer-redeeming">
                        <svg class="bzc-spin" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#0a8fc0" stroke-width="3"><path d="M12 3a9 9 0 1 1-9 9"></path></svg>
                        <span>{{ c.message }}</span>
                      </div>
                      <p v-else class="bzc-confirm-note">{{ c.message }}</p>
                      <button v-if="canOfferNewOffer(c)" type="button" class="bzc-btn bzc-btn-outline" :disabled="busy" @click="send('Please show me that purchase offer again.')">Show a new offer</button>
                    </template>
                  </div>
                </div>

                <div v-for="n in m.notCovered" :key="n.key" class="bzc-info" data-test="ask-bizminer-not-covered">
                  <div class="bzc-info-head">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"></path></svg>
                    <span>{{ n.title }}</span>
                  </div>
                  <div class="bzc-info-body">
                    <div v-if="n.label" class="bzc-confirm-product">{{ n.label }}</div>
                    <p class="bzc-confirm-note">{{ n.text }}</p>
                    <template v-if="n.reason === 'buy_in_app'">
                      <a
                        v-if="buyLink(n)"
                        class="bzc-btn bzc-btn-primary"
                        :href="buyLink(n).href"
                        :target="buyLink(n).external ? '_blank' : null"
                        :rel="buyLink(n).external ? 'noopener noreferrer' : null"
                        data-test="ask-bizminer-buy-in-app"
                        @click="openLink($event, buyLink(n))"
                      >Buy in the app</a>
                    </template>
                    <a
                      v-else
                      class="bzc-btn bzc-btn-outline"
                      :href="contact(n).href"
                      :target="contact(n).external ? '_blank' : null"
                      :rel="contact(n).external ? 'noopener noreferrer' : null"
                      data-test="ask-bizminer-contact-sales"
                      @click="openLink($event, contact(n))"
                    >Contact sales</a>
                  </div>
                </div>

                <!-- CHAT-06/b: today's report limit. The conversation goes on; only buying a report in chat is paused. -->
                <div v-if="m.purchaseLimit" class="bzc-info" data-test="ask-bizminer-purchase-limit">
                  <div class="bzc-info-head">
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"></path></svg>
                    <span>{{ m.purchaseLimit.title }}</span>
                  </div>
                  <div class="bzc-info-body">
                    <p class="bzc-confirm-note">{{ m.purchaseLimit.text }}</p>
                    <a
                      v-if="cartLink(m.purchaseLimit)"
                      class="bzc-btn bzc-btn-outline"
                      :href="cartLink(m.purchaseLimit).href"
                      :target="cartLink(m.purchaseLimit).external ? '_blank' : null"
                      :rel="cartLink(m.purchaseLimit).external ? 'noopener noreferrer' : null"
                      data-test="ask-bizminer-purchase-limit-cart"
                      @click="openCart($event, m.purchaseLimit)"
                    >Open your cart</a>
                  </div>
                </div>

                <div v-if="m.error" class="bzc-error" role="alert" data-test="ask-bizminer-error">
                  <span>{{ m.error.message }}</span>
                  <button v-if="retryOf(m, idx) === 'ask'" type="button" class="bzc-link-btn" @click="retry(m)">Retry</button>
                  <!-- A failed form submission is sent again with what was typed (the figures stay saved). -->
                  <button v-else-if="retryOf(m, idx) === 'resubmit'" type="button" class="bzc-link-btn" :disabled="busy" data-test="ask-bizminer-resubmit" @click="resubmit(m)">Try again</button>
                  <a v-if="needsSupport(m.error)" class="bzc-link-btn" :href="'mailto:' + supportEmail" data-test="ask-bizminer-support">Contact support</a>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="bzc-composer">
        <!-- CHAT-06/a: the daily message limit. The composer stays shut until the reset time. -->
        <div v-if="limitView" class="bzc-limit" role="status" data-test="ask-bizminer-limit">
          <b>{{ limitView.title }}</b>
          <span>{{ limitView.text }}</span>
        </div>
        <div class="bzc-composer-box">
          <label class="bzc-composer-label">
            <span class="bzc-sr">Message Ask Bizminer</span>
            <textarea
              ref="draftEl"
              v-model="draft"
              rows="2"
              maxlength="8000"
              :disabled="connection !== 'ready' || !!limitUntil"
              :placeholder="composerPlaceholder"
              @keydown="onKey"
            ></textarea>
          </label>
          <button type="button" class="bzc-send" aria-label="Send" :disabled="!canSend" @click="send(draft)">
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path></svg>
          </button>
        </div>
        <div class="bzc-composer-foot">
          <span>Benchmarks, not financial advice. Check the cited source.</span>
          <span v-if="leftLabel" class="bzc-left" data-test="ask-bizminer-left">{{ leftLabel }}</span>
        </div>
      </div>
      </template>
    </section>

    <client-summary v-if="summary" :state="summary" @close="closeSummary" @retry="retrySummary" />
  </div>
</template>

<script>
import {
  chatReducer,
  initialChatState,
  renderMarkdownLite,
  citationLabel,
  safeAppLink,
  messagesLeftLabel,
  messagesFromHistory,
  createConfirmTracker,
  confirmCardPatch,
  purchaseErrorText,
  isActionCard,
  cardCopy,
  actionHeadline,
  contextSwitchAction,
  canSwitchConversation,
  readEntitlement,
  isSubscriptionRequired,
  entitlementFromError,
  teaserView,
  chargeLine,
  subUserNoteLine,
  contactSalesLink,
  canOfferNewOffer,
  needsSettle,
  settleStep,
  settleLocksComposer,
  UPGRADE_URL,
  CONTACT_SALES_URL,
  TEASER_EVENTS,
  PHONE_MAX_WIDTH,
  chatFabState,
  keyboardViewportBox,
  needsNoticeText,
  noticeStandardPath,
  clearStoredDrafts,
  citationOpenLabel,
  summaryAction,
  summaryErrorText,
  retryKind,
  errorNeedsSupport,
  SUPPORT_EMAIL,
  normalizeLimits,
  limitUntilFrom,
  purchaseHoldFor,
  createLimitsGuard,
  confirmDisabled,
  shareStateEvent,
  limitReachedView,
  limitRefusal,
  normalizePurchaseLimit,
  purchaseRefusedText,
  normalizeShare,
} from './chatCore';
import posthog from 'posthog-js';
import ChatChart from './ChatChart.vue';
import ChatNoticeCard from './ChatNoticeCard.vue';
import ChatProspectsCard from './ChatProspectsCard.vue';
import ChatFormCard from './ChatFormCard.vue';
import ClientSummary from './ClientSummary.vue';
import ChatShareMenu from './ChatShareMenu.vue';
import {
  ChatApiError,
  isDenied,
  openSession,
  getMe,
  createConversation,
  getConversation,
  sendMessage,
  answerConfirmation,
  getNoticeAck,
  postNoticeAck,
  submitForm,
  getSummary,
  withReauth,
} from './chatApi';

const STORE_KEY = 'bzchat.conversation';
const BEACON_WIDTH = 400;

function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
  } catch (e) {
    return null;
  }
}
function writeStored(v) {
  try {
    if (v) sessionStorage.setItem(STORE_KEY, JSON.stringify(v));
    else sessionStorage.removeItem(STORE_KEY);
  } catch (e) {
    /* storage unavailable: history reopen just won't survive a reload */
  }
}

export default {
  name: 'ask-bizminer-panel',
  components: { ChatChart, ChatNoticeCard, ChatProspectsCard, ChatFormCard, ClientSummary, ChatShareMenu },
  props: {
    // CHAT-13: the same panel, drawn inside the /chat page instead of as the
    // fixed side sheet: no header, floating button or page chrome; the page picks
    // the conversation (`openId`, null = a new one) and listens for the events
    // `ready`, `conversation`, `changed`, `working`, `missing`.
    embedded: { type: Boolean, default: false },
    openId: { type: String, default: null },
  },
  data() {
    return {
      chat: initialChatState(),
      draft: '',
      context: null, // the report the CURRENT conversation is about (frozen with it)
      connection: 'idle', // idle | connecting | ready | failed
      connectError: '',
      uid: null,
      subUser: false,
      limits: null, // CHAT-06: {messagesLeft, messageLimit, resetAt, purchasesLeft, purchaseLimit} from /me
      limitUntil: null, // the daily message limit is reached: the composer stays shut until this time (ms)
      purchaseUntil: null, // the daily report limit is reached: Confirm stays off until this time (ms)
      sharedAt: null, // CHAT-08: when the open conversation was shared with support (null = not shared)
      shareNote: '', // the service's note on what sharing means, once seen
      working: false, // a confirm/decline call is in flight
      settleLocked: false, // the fast phase of polling: the composer waits (D3: not in the slow phase)
      settling: false, // polling GET /conversations/:id for an answer / purchase still running (F1, F2)
      notice: '', // short note above the messages ("Finishing your purchase…")
      upgradeUrl: UPGRADE_URL,
      contactUrl: CONTACT_SALES_URL,
      rechecking: false, // "Check again" on a stalled purchase is reading the conversation
      seq: 0,
      turnSeq: 0, // counts turns and clicks started here: a poll that began before one may only patch cards (D3)
      handshake: 0, // guards stale async results
      pinned: true,
      loadingConv: false, // embedded: a conversation is being read from history
      isPhone: false, // viewport is 600px wide or less (CHAT-23)
      kbBox: null, // {height, top} of the visible area while the phone keyboard is up
      summary: null, // the one-page client summary: {phase: loading|ready|error, messageId, data, error, retryable}
      supportEmail: SUPPORT_EMAIL,
    };
  },
  computed: {
    visible() {
      return this.$store.getters['chat/visible'];
    },
    // The /chat page has its own embedded panel: the floating side sheet stays shut there.
    onChatPage() {
      return !this.embedded && !!(this.$route && this.$route.meta && this.$route.meta.chatPage);
    },
    isOpen() {
      if (this.embedded) return this.visible;
      return this.$store.getters['chat/isOpen'] && !this.onChatPage;
    },
    // Changes on every open AND on every re-open while already open (a second
    // "Ask about this report" click): the one trigger for the handshake.
    openKey() {
      if (this.embedded) return this.isOpen ? 1 : 0;
      return this.isOpen ? this.$store.state.chat.openSeq : 0;
    },
    // 'full' = chat panel, 'teaser' = locked panel (CHAT-18/e). See chat/mode.
    mode() {
      return this.$store.getters['chat/mode'];
    },
    teaserRetryable() {
      return this.$store.getters['chat/teaserRetryable'];
    },
    teaserConfirmed() {
      return this.$store.getters['chat/teaserConfirmed'];
    },
    // What the teaser branch shows: checking | error | retry | pitch (see teaserView).
    teaserView() {
      return teaserView({ confirmed: this.teaserConfirmed, retryable: this.teaserRetryable, connection: this.connection });
    },
    // CHAT-23/a: the floating button (phones only, by CSS). Same rule as the top-bar button.
    fab() {
      return chatFabState({
        visible: this.visible && !this.onChatPage,
        open: this.isOpen,
        locked: this.$store.getters['chat/locked'],
      });
    },
    // While the keyboard is up the full-screen sheet shrinks to the visible area,
    // so the composer stays above the keyboard.
    panelStyle() {
      return this.kbBox ? { top: this.kbBox.top + 'px', height: this.kbBox.height + 'px', bottom: 'auto' } : null;
    },
    // An academic (IP) session has no individual uid: teaser only, no backend call.
    academicSession() {
      const t = this.$store.state.user && this.$store.state.user.user_type;
      return !!(t && t.academic);
    },
    // A turn or a click is running here.
    turnBusy() {
      return this.chat.busy || this.working;
    },
    // ...or the server is still working on one (polling): nothing new can start.
    busy() {
      return this.turnBusy || this.settleLocked;
    },
    dirty() {
      return this.draft.trim().length > 0 || this.chat.messages.length > 0;
    },
    canSend() {
      return this.connection === 'ready' && !this.busy && !this.limitUntil && this.draft.trim().length > 0;
    },
    shared() {
      return !!this.sharedAt;
    },
    // The message-limit card (null while chat is open).
    limitView() {
      return this.limitUntil ? limitReachedView(this.limitUntil) : null;
    },
    purchaseBlocked() {
      return !!this.purchaseUntil;
    },
    composerPlaceholder() {
      if (this.limitUntil) return 'Ask Bizminer is paused until the limit resets\u2026';
      return this.embedded && this.chat.messages.length ? 'Continue this conversation\u2026' : 'Ask about industries, markets, companies or your profiles and reports\u2026';
    },
    canDropContext() {
      return !this.chat.messages.length;
    },
    leftLabel() {
      return messagesLeftLabel(this.limits && this.limits.messagesLeft);
    },
    starters() {
      return this.context
        ? [
            'What does this Industry Financial Profile say about profitability?',
            'How have margins changed over the last five years?',
            'Which profiles and reports do I already own for this industry?',
          ]
        : [
            'Which profiles and reports do I already own?',
            'What is a typical gross margin for restaurants?',
            'How do two metro areas compare for my industry?',
          ];
    },
  },
  watch: {
    // Every open (and every re-open from "Ask about this report") re-does the
    // assertion -> POST /session handshake: a stale cookie from an earlier
    // open may belong to a previous app user.
    isOpen(open) {
      this.syncChrome(open);
      this.onViewport();
      if (!open) this.handshake++; // drop any handshake still in flight
    },
    'fab.show'() {
      this.syncChrome(this.isOpen);
    },
    openKey(key) {
      if (key) this.connect();
    },
    visible(v) {
      if (!v) this.wipe();
    },
    // The page picks the conversation; the panel follows once it is connected
    // (connect() reads `openId` itself when it gets there).
    openId(id) {
      this.switchTo(id || null);
    },
    // The page keeps its list still while anything runs here (a stream, polling, a purchase click).
    busy(v) {
      if (this.embedded) this.$emit('working', !!v);
    },
    // Something is on screen that a page-side auto-open must not replace: a draft or messages.
    dirty(v) {
      if (this.embedded) this.$emit('dirty', !!v);
    },
  },
  created() {
    // Per-conversation epoch + abort controller (not reactive on purpose). A
    // reset, wipe or logout bumps the epoch and aborts in-flight requests; any
    // async work that captured an older epoch must not touch state or storage.
    this.epoch = 0;
    this.abortCtl = new AbortController();
    this.lastSubmit = {}; // form id -> the values last submitted, for "Try again" (never stored)
    this.noticeBusy = false;
    this.limitTimers = {}; // limitUntil / purchaseUntil -> the timer that lifts it
    this.limitsGuard = createLimitsGuard(); // orders /me reads against the events that set a hold
  },
  mounted() {
    this.watchViewport();
    if (!this.embedded) {
      this.watchBeacon();
      document.body.classList.toggle('bzc-fab-on', this.fab.show);
    }
    if (this.isOpen) {
      this.syncChrome(true);
      this.connect();
    }
  },
  beforeDestroy() {
    this.abortCtl.abort();
    this.clearHolds();
    this.unwatchViewport();
    // The page keeps its list still while this says busy or dirty: it must not wait for a "done" that never comes.
    if (this.embedded) {
      this.$emit('working', false);
      this.$emit('dirty', false);
      return;
    } // the page chrome belongs to the floating panel
    this.unwatchBeacon();
    this.syncChrome(false);
    document.body.classList.remove('bzc-fab-on', 'bzc-beacon-on', 'bzc-phone-open');
  },
  methods: {
    // --- page chrome: shrink the page beside the panel, move the Help Scout beacon
    syncChrome(open) {
      if (this.embedded) return; // embedded in the page: nothing to shrink or move
      document.body.classList.toggle('bzc-open', !!open);
      // The floating button sits where the Help Scout beacon does: the button lifts above it (CSS, phones only).
      document.body.classList.toggle('bzc-fab-on', this.fab.show);
      const phone = !!(this.mq && this.mq.matches);
      this.syncPhoneOpen(open, phone);
      try {
        // Phones hide the beacon outright while the sheet is open (CSS): nothing to offset.
        if (window.Beacon && !(open && phone)) {
          window.Beacon('config', { display: { horizontalOffset: open ? BEACON_WIDTH : 0 } });
        }
      } catch (e) {
        /* beacon not loaded (blocked): nothing to move */
      }
    },
    // Phone sheet open: the beacon (a third-party iframe with a huge z-index)
    // would sit on top of the full-screen sheet, so CSS hides it via this class.
    syncPhoneOpen(open, phone) {
      document.body.classList.toggle('bzc-phone-open', !!(open && phone));
    },
    // The beacon mounts itself some time after page load; track whether its
    // #beacon-container exists so the floating button can lift above it.
    syncBeaconClass() {
      document.body.classList.toggle('bzc-beacon-on', !!document.getElementById('beacon-container'));
    },
    watchBeacon() {
      this.syncBeaconClass();
      if (typeof MutationObserver === 'undefined') return;
      this.beaconObserver = new MutationObserver(this.syncBeaconClass);
      this.beaconObserver.observe(document.body, { childList: true });
    },
    unwatchBeacon() {
      if (this.beaconObserver) this.beaconObserver.disconnect();
      this.beaconObserver = null;
    },
    close() {
      this.$store.dispatch('chat/closePanel');
    },
    // The floating button opens the panel the same way the top-bar button does
    // (no report forced: the panel keeps its own conversation).
    openFromFab() {
      this.$store.dispatch('chat/openPanel', null);
    },

    // --- phones (CHAT-23/a) ------------------------------------------------------
    // 600px and below the panel is a full-screen sheet. Phone browsers do not
    // resize the page for the on-screen keyboard (only the visual viewport
    // shrinks), so the sheet follows window.visualViewport while the keyboard is up.
    watchViewport() {
      const vv = window.visualViewport;
      this.mq = window.matchMedia ? window.matchMedia('(max-width: ' + PHONE_MAX_WIDTH + 'px)') : null;
      if (this.mq) {
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.onViewport);
        else if (this.mq.addListener) this.mq.addListener(this.onViewport);
      }
      if (vv) {
        vv.addEventListener('resize', this.onViewport);
        vv.addEventListener('scroll', this.onViewport);
      }
      this.onViewport();
    },
    unwatchViewport() {
      const vv = window.visualViewport;
      if (this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onViewport);
        else if (this.mq.removeListener) this.mq.removeListener(this.onViewport);
      }
      if (vv) {
        vv.removeEventListener('resize', this.onViewport);
        vv.removeEventListener('scroll', this.onViewport);
      }
    },
    onViewport() {
      this.isPhone = !!(this.mq && this.mq.matches);
      if (!this.embedded) this.syncPhoneOpen(this.isOpen, this.isPhone);
      const vv = window.visualViewport;
      this.kbBox =
        this.isPhone && this.isOpen && !this.embedded && vv
          ? keyboardViewportBox({ layoutHeight: window.innerHeight, viewportHeight: vv.height, viewportTop: vv.offsetTop })
          : null;
      if (this.kbBox) this.scrollDown(false);
    },

    // --- session handshake ------------------------------------------------
    async connect() {
      let token = ++this.handshake;
      if (this.academicSession) {
        this.connection = 'idle'; // the teaser needs no session
        this.dropQuestion();
        return;
      }
      this.connection = 'connecting';
      this.connectError = '';
      try {
        const s = await openSession();
        if (token !== this.handshake) return;
        const ticket = this.limitsGuard.begin();
        const me = await getMe().catch(() => null);
        if (token !== this.handshake) return;
        const uid = s && s.uid != null ? s.uid : me && me.uid;
        if (this.uid != null && uid != null && uid !== this.uid) {
          // Different person, same browser. wipe() bumps the handshake, so take
          // a fresh token or every check below would think this connect is stale.
          this.wipe();
          token = this.handshake;
          this.connection = 'connecting';
        }
        this.uid = uid != null ? uid : this.uid;
        this.subUser = !!((s && s.subUser) || (me && me.subUser) || this.appSubUser());
        // The server's entitlement is authoritative over the client's guess.
        await this.takeEntitlement(s, me);
        if (token !== this.handshake) return;
        if (this.mode === 'teaser') {
          this.dropQuestion(); // a teaser has no composer: nothing to send
          if (!this.teaserConfirmed) {
            // The service gave no entitlement: the guess stays a guess, so no pitch (P2).
            this.connection = 'failed';
            this.connectError = "Couldn't check your access. Please try again.";
            return;
          }
          this.connection = 'ready';
          return;
        }
        this.applyLimits(me, ticket);
        if (this.embedded) {
          // The /chat page picks the conversation; a question waiting in the store belongs to the side sheet.
          this.dropQuestion();
          if (this.openId) await this.openById(this.openId, token);
        } else {
          await this.applyContext(this.$store.state.chat.context, token);
        }
        if (token !== this.handshake) return;
        this.connection = 'ready';
        if (this.embedded) {
          this.$emit('ready');
          // The page may have picked another conversation while this one was opening.
          if ((this.openId || null) !== (this.chat.conversationId || null)) this.switchTo(this.openId || null);
        } else {
          this.$nextTick(() => this.$refs.draftEl && this.$refs.draftEl.focus());
          this.sendPendingQuestion();
        }
      } catch (e) {
        if (token !== this.handshake) return;
        this.dropQuestion();
        this.onConnectError(e);
      }
    },
    // CHAT-20: "Ask about this" on an insight opens the panel with the question to send.
    // If something is still running, the question waits in the composer instead.
    async sendPendingQuestion() {
      const q = await this.$store.dispatch('chat/takeQuestion');
      if (!q) return;
      if (this.busy || this.connection !== 'ready') {
        this.draft = q;
        return;
      }
      await this.send(q);
    },
    dropQuestion() {
      this.$store.commit('chat/CLEAR_QUESTION');
    },
    takeEntitlement(s, me) {
      const a = readEntitlement(s);
      const pick = a.entitlement ? a : readEntitlement(me);
      if (!pick.entitlement) return Promise.resolve();
      return this.$store.dispatch('chat/setEntitlement', { entitlement: pick.entitlement, retryable: pick.retryable });
    },
    // subscription_required (403 or the SSE error): show the teaser from now on.
    // `x` is the ChatApiError or the SSE error data: `retryable: true` means the
    // subscription could not be read, so the panel says "Try again", not the pitch (P1).
    becomeTeaser(x) {
      this.connection = 'ready';
      return this.$store.dispatch('chat/setEntitlement', entitlementFromError(x));
    },
    // D2: one PostHog event per teaser button. Best effort.
    trackTeaser(which) {
      try {
        posthog.capture(TEASER_EVENTS[which]);
      } catch (e) {
        /* tracking is best effort */
      }
    },
    onConnectError(e) {
      if (isSubscriptionRequired(e)) {
        this.becomeTeaser(e);
        return;
      }
      if (isDenied(e)) {
        // Not on the beta list (not_in_beta) or refused by api.app (not_allowed):
        // hide every entry point, silently, for the rest of the session.
        this.$store.dispatch('chat/deny');
        return;
      }
      this.connection = 'failed';
      this.connectError =
        e instanceof ChatApiError && e.status === 503
          ? 'Ask Bizminer is unavailable right now. Please try again in a moment.'
          : "Couldn't start Ask Bizminer. Please try again.";
    },
    appSubUser() {
      const u = this.$store.state.user && this.$store.state.user.user;
      return !!(u && u.is_sub_user);
    },
    // /me and /session carry `limits`. The counter only ever shuts the composer (at 0 left, until resetAt);
    // it is the timer, never a recount, that opens it again: a cost limit can hold while messages remain.
    applyLimits(me, ticket) {
      const l = normalizeLimits(me);
      if (!l) return;
      if (!this.limitsGuard.accept(ticket)) return; // an older read than one already applied
      this.limits = l;
      const until = limitUntilFrom(l);
      if (until) this.holdUntil('limitUntil', until);
      // The purchase hold follows the real count: above 0 lifts it (a counted claim failed or was reaped),
      // 0 holds it until resetAt. An unknown count leaves it alone.
      // A read issued before an event set the hold cannot lift it.
      const purchase = purchaseHoldFor(l, this.limitsGuard, ticket);
      if (purchase !== undefined) this.holdUntil('purchaseUntil', purchase);
    },
    // Confirm / "Try again": a fresh card is off at the report limit, a card the user already clicked is not.
    confirmOff(c) {
      return confirmDisabled(c, { busy: this.busy, purchaseBlocked: this.purchaseBlocked });
    },
    // Keeps `key` (limitUntil | purchaseUntil) set until the time passes, then lifts it. A time that has
    // already passed holds nothing.
    holdUntil(key, until) {
      clearTimeout(this.limitTimers[key]);
      if (typeof until !== 'number' || until <= Date.now()) {
        this[key] = null;
        return;
      }
      this[key] = until;
      const tick = () => {
        const left = this[key] == null ? 0 : this[key] - Date.now();
        if (left > 0) {
          this.limitTimers[key] = setTimeout(tick, Math.min(left + 250, 2147483647)); // setTimeout caps at ~24.8 days
          return;
        }
        this[key] = null;
        if (key === 'limitUntil') this.refreshLimits();
      };
      this.limitTimers[key] = setTimeout(tick, Math.min(until - Date.now() + 250, 2147483647));
    },
    clearHolds() {
      Object.keys(this.limitTimers).forEach((k) => clearTimeout(this.limitTimers[k]));
      this.limitTimers = {};
    },
    // The `purchase_limit` SSE event: no more reports can be added from chat for now.
    notePurchaseLimit(data) {
      const p = normalizePurchaseLimit(data);
      if (p && p.resetAt) this.holdPurchases(p.resetAt);
    },
    // The report limit was just reported by the service (the event, or a 429): hold Confirm, and mark the
    // moment so a /me issued before it cannot lift the hold.
    holdPurchases(resetAt) {
      this.limitsGuard.noteHold('purchaseUntil');
      this.holdUntil('purchaseUntil', resetAt);
    },
    async refreshLimits() {
      const epoch = this.epoch;
      const ticket = this.limitsGuard.begin();
      try {
        const me = await getMe();
        if (epoch !== this.epoch) return; // a reset or wipe happened while it was out
        this.applyLimits(me, ticket);
      } catch (e) {
        /* counter is informational */
      }
    },

    // --- context & conversation ------------------------------------------
    // A different report starts a new conversation (the context is frozen into
    // a conversation when it is created). No new context: keep what we have,
    // or reopen the conversation stored for this browser tab.
    async applyContext(ctx, token) {
      const act = contextSwitchAction({ next: ctx, current: this.context, working: this.working });
      if (act === 'refuse') {
        // A purchase is in flight: resetting would abort its stream (F3).
        this.notice = 'Finishing your purchase\u2026 Ask about the other profile again in a moment.';
        return;
      }
      if (act === 'reset') {
        this.resetConversation();
        this.context = ctx;
        return;
      }
      if (!ctx && !this.chat.conversationId && !this.chat.messages.length) {
        const stored = readStored();
        if (stored && stored.id && stored.uid === this.uid) {
          const r = await this.loadHistory(stored.id, token);
          if (r === 'gone' || r === 'error') this.persist(null); // gone (404) or unreadable: start clean
        }
      }
    },
    // Reads one conversation from history into the panel. Resolves 'ok' | 'gone'
    // (404) | 'error' | 'stale' (a reset or a newer connect got there first).
    // Throws only when the service says this account may not use chat.
    async loadHistory(id, token) {
      const epoch = this.epoch;
      const current = () => epoch === this.epoch && token === this.handshake;
      try {
        const conv = await getConversation(id, this.abortCtl.signal);
        if (!current()) return 'stale';
        this.dispatch({
          type: 'history',
          conversationId: conv.id,
          messages: messagesFromHistory(conv.messages),
        });
        this.context = conv.context || null;
        this.takeShare(conv);
        this.scrollDown(true);
        this.settleLoop(epoch); // an answer or purchase still running on the server (F1, F2)
        return 'ok';
      } catch (e) {
        if (!current()) return 'stale';
        if (isDenied(e)) throw e;
        return e instanceof ChatApiError && e.status === 404 ? 'gone' : 'error';
      }
    },
    // CHAT-13 (embedded): show the conversation the page picked. The panel is
    // already on it after a new conversation was created here, so nothing reloads.
    async openById(id, token) {
      const mine = (this.loadSeq = (this.loadSeq || 0) + 1); // only the newest open clears the spinner
      this.loadingConv = true;
      try {
        const r = await this.loadHistory(id, token);
        if (r === 'gone') this.$emit('missing', id);
        else if (r === 'error') this.notice = "Couldn't open this conversation. Pick it again to retry.";
      } finally {
        if (mine === this.loadSeq) this.loadingConv = false;
      }
    },
    async switchTo(id) {
      if (!this.embedded || this.connection !== 'ready' || this.mode !== 'full') return;
      const here = this.chat.conversationId || null;
      if (id === here && (id || !this.chat.messages.length)) return;
      // Switching mid-answer would abort it: the page disables the list, and the panel refuses too.
      if (!canSwitchConversation({ busy: this.chat.busy, settleLocked: this.settleLocked, working: this.working })) {
        // Refused: tell the page which conversation is really open so its selection does not drift from it.
        this.$emit('conversation', here);
        return;
      }
      this.resetConversation();
      this.draft = '';
      if (!id) return;
      try {
        await this.openById(id, this.handshake);
      } catch (e) {
        if (isDenied(e)) this.$store.dispatch('chat/deny');
      }
    },
    // Only the floating panel remembers its conversation across a reload.
    persist(v) {
      if (!this.embedded) writeStored(v);
    },
    // A new epoch: whatever a previous conversation still has in flight is
    // aborted, and its late results are ignored (see runTurn / answer).
    newEpoch() {
      this.epoch += 1;
      this.abortCtl.abort();
      this.abortCtl = new AbortController();
      this.working = false;
      this.settling = false;
      this.settleLocked = false;
      this.notice = '';
      this.summary = null;
      this.lastSubmit = {};
      this.noticeBusy = false;
    },
    resetConversation() {
      this.newEpoch();
      this.dispatch({ type: 'reset' });
      this.clearShare();
      this.context = null;
      this.persist(null);
    },
    newConversation() {
      if (this.turnBusy) return;
      this.resetConversation();
      this.draft = '';
    },
    // Only before the first message: a conversation's context is frozen when
    // it is created, so a started thread keeps its report.
    dropContext() {
      if (!this.canDropContext) return;
      this.context = null;
      this.chat = { ...this.chat, conversationId: null };
      this.clearShare();
      this.persist(null);
    },
    // Logout, user change or loss of access: forget everything.
    wipe() {
      this.handshake++;
      this.newEpoch();
      this.chat = initialChatState();
      this.context = null;
      this.uid = null;
      this.subUser = false;
      this.clearHolds();
      this.limitsGuard = createLimitsGuard(); // a read still out belongs to the old session (wipe also bumps the epoch)
      this.limits = null;
      this.limitUntil = null;
      this.purchaseUntil = null;
      this.clearShare();
      this.draft = '';
      this.connection = 'idle';
      this.persist(null);
      this.forgetDrafts(); // the customer's own figures stay with the person who typed them
    },

    // --- sending ------------------------------------------------------------
    dispatch(action) {
      this.chat = chatReducer(this.chat, action);
      this.scrollDown(false);
      // A form that needs the notice but has not got its text (reopened from history, or refused for it).
      const wantsNotice = action.type === 'history' || action.type === 'poll' || action.type === 'noticeRequired' || (action.type === 'event' && action.name === 'form');
      if (wantsNotice && needsNoticeText(this.chat.messages)) this.loadNotice();
    },
    nextId(p) {
      this.seq += 1;
      return p + this.seq;
    },
    onKey(e) {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        if (this.canSend) this.send(this.draft);
      }
    },
    async send(text, opts = {}) {
      text = String(text || '').trim();
      if (!text || this.busy || this.connection !== 'ready' || this.limitUntil) return;
      this.draft = '';
      this.turnSeq += 1;
      this.dispatch({ type: 'user', id: this.nextId('u'), text });
      await this.runTurn(text, opts);
    },
    // One question -> one streamed answer. `again` marks the silent
    // reauth_required re-send so it happens at most once.
    async runTurn(text, { again = false } = {}) {
      const epoch = this.epoch;
      const signal = this.abortCtl.signal;
      const live = () => epoch === this.epoch;
      const aid = this.nextId('a');
      this.dispatch({ type: 'assistant', id: aid });
      let reauth = false;
      let subRequired = null; // the subscription_required error data, once seen
      const onEvent = (ev) => {
        if (!live()) return;
        if (ev.name === 'error' && isSubscriptionRequired(ev.data)) {
          subRequired = ev.data;
          return; // ends the turn: the teaser replaces the panel
        }
        if (ev.name === 'error' && ev.data && ev.data.code === 'reauth_required') {
          reauth = true;
          return; // handled below, never shown
        }
        if (reauth && ev.name === 'done') return;
        if (ev.name === 'purchase_limit') this.notePurchaseLimit(ev.data);
        this.dispatch({ type: 'event', id: aid, name: ev.name, data: ev.data });
      };
      try {
        const cid = await this.ensureConversation(epoch, signal);
        if (!cid || !live()) return;
        await withReauth(() => sendMessage(cid, text, onEvent, signal));
        if (!live()) return;
        if (subRequired) {
          this.dispatch({ type: 'streamEnd', id: aid });
          this.dispatch({ type: 'drop', id: aid });
          await this.becomeTeaser(subRequired);
          return;
        }
        if (reauth && !again) {
          // The user's MCP token went bad mid-turn: new assertion, same question, once.
          await openSession();
          if (!live()) return;
          this.dispatch({ type: 'drop', id: aid });
          return this.runTurn(text, { again: true });
        }
        if (reauth) {
          this.dispatch({ type: 'event', id: aid, name: 'error', data: { code: 'reauth_required', message: 'Your session needs to be refreshed. Reload the page and try again.', retryable: true } });
        }
        // No `done` while still streaming = cut off: marked interrupted, Retry offered.
        this.dispatch({ type: 'streamEnd', id: aid });
      } catch (e) {
        if (!live()) return;
        this.onTurnError(aid, e, text);
      }
      if (live()) {
        this.refreshLimits();
        if (this.embedded) this.$emit('changed', this.chat.conversationId);
      }
    },
    onTurnError(aid, e, text) {
      if (isSubscriptionRequired(e)) {
        this.dispatch({ type: 'fail', id: aid });
        this.dispatch({ type: 'drop', id: aid });
        this.becomeTeaser(e);
        return;
      }
      if (isDenied(e)) {
        this.$store.dispatch('chat/deny');
        return;
      }
      if (e instanceof ChatApiError) {
        if (e.status === 404) {
          this.chat = { ...this.chat, conversationId: null };
          this.clearShare();
          this.persist(null);
          return this.dispatch({ type: 'fail', id: aid, code: 'not_found', message: 'That conversation is no longer available. Try your question again to start a new one.', retryable: true });
        }
        if (e.code === 'turn_in_progress') {
          return this.dispatch({ type: 'fail', id: aid, code: e.code, message: 'Ask Bizminer is still answering your last question. Give it a moment.', retryable: true });
        }
        // CHAT-06/a: the daily message limit. Nothing was stored or sent: the question goes back to the
        // composer, which stays shut until resetAt, and a card says when it opens.
        const refusal = limitRefusal(e);
        if (refusal && refusal.kind === 'message' && refusal.resetAt && refusal.resetAt > Date.now()) {
          return this.messageLimitRefused(aid, refusal.resetAt, text);
        }
        if (e.status === 429) {
          return this.dispatch({ type: 'fail', id: aid, code: 'rate_limited', message: e.message && e.message !== 'rate_limited' ? e.message : "You've reached a limit for now. Please try again later.", retryable: false });
        }
      }
      this.dispatch({ type: 'fail', id: aid, code: 'connection', message: 'The connection was interrupted. You can try again.', retryable: true });
    },
    messageLimitRefused(aid, resetAt, text) {
      const list = this.chat.messages;
      const at = list.findIndex((x) => x.id === aid);
      const asked = at > 0 && list[at - 1].role === 'user' ? list[at - 1] : null;
      this.dispatch({ type: 'fail', id: aid });
      this.dispatch({ type: 'drop', id: aid });
      if (asked) this.dispatch({ type: 'drop', id: asked.id });
      if (text && !this.draft) this.draft = text;
      this.holdUntil('limitUntil', resetAt);
    },
    // Resolves to the conversation id, or null when the conversation was
    // reset/wiped while the request was in flight (then nothing is stored).
    async ensureConversation(epoch, signal) {
      if (this.chat.conversationId) return this.chat.conversationId;
      const created = await withReauth(() => createConversation(this.context, signal));
      if (epoch !== this.epoch) return null;
      this.dispatch({ type: 'conversation', conversationId: created.id });
      this.persist({ uid: this.uid, id: created.id });
      if (this.embedded) {
        this.$emit('conversation', created.id);
        this.$emit('changed', created.id); // the new conversation is listed now, not only when the turn ends
      }
      return created.id;
    },
    // Retry re-asks the question above the answer, so it only exists when the
    // answer directly follows a user message (not for the answer to a card click).
    canRetry(idx) {
      const prev = this.chat.messages[idx - 1];
      return !this.busy && idx === this.chat.messages.length - 1 && !!prev && prev.role === 'user';
    },
    retry(m) {
      const i = this.chat.messages.findIndex((x) => x.id === m.id);
      const prev = this.chat.messages[i - 1];
      if (!prev || prev.role !== 'user' || this.busy || this.limitUntil || this.connection !== 'ready') return;
      this.dispatch({ type: 'drop', id: m.id });
      this.dispatch({ type: 'drop', id: prev.id });
      this.send(prev.text);
    },

    // --- Financial / Valuation / Market Report in chat (CHAT-11, CHAT-22) ----------------------
    forgetDrafts() {
      try {
        clearStoredDrafts(window.sessionStorage);
      } catch (e) {
        /* storage unavailable: nothing was kept */
      }
    },
    findForm(id) {
      for (const m of this.chat.messages) {
        const f = (m.forms || []).find((x) => x.id === id);
        if (f) return f;
      }
      return null;
    },
    standardPath(m) {
      return noticeStandardPath(m);
    },
    // "Use the standard form instead": the app's own report form. On a phone the sheet covers the page, so it closes.
    openStandard(e, path) {
      this.openLink(e, { external: false, href: path });
      if (this.isPhone && !this.embedded) this.close();
    },
    // The notice text, read from GET /notice-acks (a form reopened from history, or one refused for the notice).
    async loadNotice() {
      if (this.noticeBusy) return;
      const epoch = this.epoch;
      this.noticeBusy = true;
      try {
        const body = await withReauth(() => getNoticeAck(this.abortCtl.signal));
        if (epoch !== this.epoch) return;
        this.dispatch({ type: 'noticeLoaded', notice: body });
      } catch (e) {
        if (epoch !== this.epoch) return;
        if (isDenied(e)) return this.$store.dispatch('chat/deny');
        this.dispatch({ type: 'noticeLoadFailed' });
      } finally {
        if (epoch === this.epoch) this.noticeBusy = false;
      }
      // Another notice may have started waiting while this one was read.
      if (epoch === this.epoch && needsNoticeText(this.chat.messages)) this.loadNotice();
    },
    reloadNotice() {
      this.dispatch({ type: 'noticeLoading' });
      this.loadNotice();
    },
    // "Continue": acknowledge the notice, then the form that waited behind it opens.
    async ackNotice() {
      let version = null;
      for (const m of this.chat.messages) if (m.notice && m.notice.status === 'open' && m.notice.version) version = m.notice.version;
      if (!version || this.busy) return;
      const epoch = this.epoch;
      this.dispatch({ type: 'noticeAcking' });
      try {
        await withReauth(() => postNoticeAck(version, this.abortCtl.signal));
        if (epoch !== this.epoch) return;
        this.dispatch({ type: 'noticeAcked' });
        this.$nextTick(() => {
          const el = this.$el && this.$el.querySelectorAll ? [].slice.call(this.$el.querySelectorAll('[data-test=ask-bizminer-form]')).pop() : null;
          if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
        });
      } catch (e) {
        if (epoch !== this.epoch) return;
        if (isDenied(e)) return this.$store.dispatch('chat/deny');
        if (isSubscriptionRequired(e)) {
          this.dispatch({ type: 'noticeFailed' });
          return this.becomeTeaser(e);
        }
        if (e instanceof ChatApiError && e.code === 'unknown_version') {
          this.reloadNotice(); // the notice was updated: read the new one
          return;
        }
        this.dispatch({ type: 'noticeFailed' });
      }
    },
    // The form's Submit: save, then run. The service answers with form_result, then the normal Confirm card
    // (a first run) or the answer itself (a report already owned). Anything unclear is settled by re-reading
    // the conversation; nothing is charged by a submission.
    async submitFormCard(f, values) {
      const conversationId = this.chat.conversationId;
      if (!f || this.busy || !conversationId || this.connection !== 'ready') return;
      const epoch = this.epoch;
      const signal = this.abortCtl.signal;
      const live = () => epoch === this.epoch;
      let aid = null; // the answer that follows the form
      let subRequired = null;
      let reauth = false;
      this.lastSubmit[f.id] = values;
      this.working = true;
      this.turnSeq += 1;
      this.notice = '';
      this.dispatch({ type: 'formPhase', formId: f.id, phase: 'submitting' });
      const onEvent = (ev) => {
        if (!live()) return;
        if (ev.name === 'error' && isSubscriptionRequired(ev.data)) {
          subRequired = ev.data;
          return;
        }
        if (ev.name === 'error' && ev.data && ev.data.code === 'reauth_required') {
          reauth = true; // handled below, never shown
          return;
        }
        if (ev.name === 'form_result') return this.dispatch({ type: 'event', name: ev.name, data: ev.data });
        if (reauth && ev.name === 'done') return;
        if (ev.name === 'purchase_limit') this.notePurchaseLimit(ev.data);
        if (!aid) {
          aid = this.nextId('a');
          this.dispatch({ type: 'assistant', id: aid, formRef: f.id });
        }
        this.dispatch({ type: 'event', id: aid, name: ev.name, data: ev.data });
      };
      try {
        await withReauth(() => submitForm(conversationId, f.id, values, onEvent, signal));
        if (!live()) return;
        if (aid) this.dispatch({ type: 'streamEnd', id: aid });
        if (subRequired) {
          this.dispatch({ type: 'formPhase', formId: f.id, phase: 'editing' });
          await this.becomeTeaser(subRequired);
        } else if (reauth) {
          try {
            await openSession(); // renew the cookie so submitting again can succeed
          } catch (e) {
            /* the next submit will show the real error */
          }
          if (live()) this.dispatch({ type: 'formFailed', formId: f.id, message: 'Your session was refreshed. Please submit the form again.' });
        }
      } catch (e) {
        if (!live()) return;
        if (aid) this.dispatch({ type: 'streamEnd', id: aid });
        await this.onSubmitError(f, e, epoch);
      }
      if (live()) {
        this.dispatch({ type: 'formPhase', formId: f.id, phase: 'editing' });
        this.working = false;
        this.notice = '';
        this.refreshLimits();
        if (this.embedded) this.$emit('changed', this.chat.conversationId);
      }
    },
    async onSubmitError(f, e, epoch) {
      const fail = (message) => this.dispatch({ type: 'formFailed', formId: f.id, message });
      if (isSubscriptionRequired(e)) {
        this.dispatch({ type: 'formPhase', formId: f.id, phase: 'editing' });
        return this.becomeTeaser(e);
      }
      if (isDenied(e)) {
        this.$store.dispatch('chat/deny');
        return;
      }
      if (e instanceof ChatApiError) {
        if (e.status === 400 && e.code === 'invalid_inputs') return this.dispatch({ type: 'formInvalid', formId: f.id, fields: e.fields });
        if (e.status === 403 && e.code === 'notice_required') return this.dispatch({ type: 'noticeRequired', formId: f.id });
        if (e.code === 'turn_in_progress') return fail('Ask Bizminer is still answering your last question. Give it a moment, then submit again.');
        if (e.status === 404) return fail('This form is no longer available. Ask for the report again to get a new one.');
        const refusal = limitRefusal(e);
        if (refusal && refusal.kind === 'message') {
          this.holdUntil('limitUntil', refusal.resetAt);
          return fail(limitReachedView(refusal.resetAt).text);
        }
        if (e.status === 429) return fail(e.message && e.message !== 'rate_limited' ? e.message : "You've reached a limit for now. Please try again later.");
        if (e.status === 400) return fail("That couldn't be sent. Check the figures and submit again.");
      }
      // A dropped connection or a server error: what happened is unknown here, so read it back from the service.
      await this.reloadConversation(epoch);
      if (epoch !== this.epoch) return;
      fail("The connection was interrupted. If your report isn't below, submit the form again.");
    },
    // The retry button of a failed answer: a question is asked again, a form submission is sent again.
    retryOf(m, idx) {
      return retryKind({ error: m.error, canAsk: this.canRetry(idx), formId: m.formRef, hasValues: !!(m.formRef && this.lastSubmit[m.formRef]) });
    },
    needsSupport(error) {
      return !!error && errorNeedsSupport(error.code);
    },
    resubmit(m) {
      const f = this.findForm(m.formRef);
      const values = this.lastSubmit[m.formRef];
      if (!f || !values || this.busy) return;
      this.dispatch({ type: 'drop', id: m.id });
      this.submitFormCard(f, values);
    },

    // --- one-page client summary (CHAT-11, Lender demo) ------------------------------
    summaryOf(m) {
      return summaryAction({ message: m, conversationId: this.chat.conversationId, busy: this.busy });
    },
    async openSummary(m) {
      if (this.summaryOf(m) !== 'available' || (this.summary && this.summary.phase === 'loading')) return;
      const conversationId = this.chat.conversationId;
      const epoch = this.epoch;
      this.summary = { phase: 'loading', messageId: m.serverId, data: null, error: '', retryable: false };
      try {
        const data = await withReauth(() => getSummary(conversationId, m.serverId, this.abortCtl.signal));
        if (epoch !== this.epoch) return;
        this.summary = { phase: 'ready', messageId: m.serverId, data, error: '', retryable: false };
      } catch (e) {
        if (epoch !== this.epoch) return;
        if (isDenied(e)) {
          this.summary = null;
          return this.$store.dispatch('chat/deny');
        }
        if (isSubscriptionRequired(e)) {
          this.summary = null;
          return this.becomeTeaser(e);
        }
        const gone = e instanceof ChatApiError && e.status === 404;
        this.summary = { phase: 'error', messageId: m.serverId, data: null, error: summaryErrorText(e), retryable: !gone };
      }
    },
    retrySummary() {
      const id = this.summary && this.summary.messageId;
      const m = this.chat.messages.find((x) => x.serverId === id);
      this.summary = null;
      if (m) this.openSummary(m);
    },
    closeSummary() {
      this.summary = null;
    },
    openLabel(c) {
      return citationOpenLabel(c);
    },

    // --- Confirm card (PFD-217 endpoints) ------------------------------------
    // A click always gets HTTP 200 + SSE. What happened is in the events:
    //   confirm_result {outcome, charged, message}  -> the card follows `outcome`
    //   error (purchase_failed | purchase_unconfirmed | reauth_required)
    //                                               -> the card is clickable again
    // Anything unclear (409, dropped stream, no outcome) is settled by re-reading
    // the card from GET /conversations/:id; the panel never guesses a charge.
    async answer(c, action) {
      const conversationId = this.chat.conversationId;
      if (!c || this.busy || !conversationId) return;
      const epoch = this.epoch;
      const signal = this.abortCtl.signal;
      const live = () => epoch === this.epoch;
      const tracker = createConfirmTracker(c.pendingId);
      let aid = null; // the answer that continues after the click
      let subRequired = null; // the subscription_required error data, once seen
      this.working = true;
      this.turnSeq += 1;
      this.notice = '';
      // A Confirm click is being sent: the card now counts as clicked (a Decline leaves that as it was).
      this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: action === 'confirm' ? 'buying' : 'declining', ...(action === 'confirm' ? { confirmClicked: true } : {}) });
      const onEvent = (ev) => {
        if (!live()) return;
        if (ev.name === 'error' && isSubscriptionRequired(ev.data)) {
          subRequired = ev.data;
          return;
        }
        if (ev.name === 'purchase_limit') this.notePurchaseLimit(ev.data);
        const kind = tracker.see(ev.name, ev.data);
        if (kind === 'result') return this.dispatch({ type: 'event', name: ev.name, data: ev.data });
        if (kind === 'held' || (ev.name === 'done' && !aid)) return;
        if (!aid) {
          aid = this.nextId('a');
          this.dispatch({ type: 'assistant', id: aid });
        }
        this.dispatch({ type: 'event', id: aid, name: ev.name, data: ev.data });
      };
      try {
        await withReauth(() => answerConfirmation(conversationId, c.pendingId, action, onEvent, signal));
        if (!live()) return;
        const v = tracker.verdict();
        if (aid) this.dispatch({ type: 'streamEnd', id: aid });
        if (subRequired) {
          this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: null, confirmClicked: !!c.confirmClicked });
          await this.becomeTeaser(subRequired);
        } else if (v.kind === 'result') {
          // F4: "re-offered" only when a new card really arrived in this stream (known at done).
          if (v.reoffered) this.dispatch({ type: 'reoffer', pendingId: c.pendingId });
          if (!v.done) await this.settleCard(c, action, epoch); // the answer was cut off: re-read it
        } else if (v.kind === 'error') {
          await this.cardFailed(c, action, v.error);
        } else {
          await this.settleCard(c, action, epoch);
        }
      } catch (e) {
        if (!live()) return;
        if (aid) this.dispatch({ type: 'streamEnd', id: aid });
        await this.onConfirmError(c, action, e, epoch);
      }
      if (live()) {
        this.working = false;
        this.notice = '';
        this.refreshLimits();
        if (this.embedded) this.$emit('changed', this.chat.conversationId);
      }
    },
    async onConfirmError(c, action, e, epoch) {
      if (isSubscriptionRequired(e)) {
        this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: null, confirmClicked: !!c.confirmClicked });
        return this.becomeTeaser(e);
      }
      if (isDenied(e)) {
        this.$store.dispatch('chat/deny');
        return;
      }
      // A card offered before the notice version changed: nothing was sent or charged. The notice shows on the
      // card's answer, the card stays pending, and Confirm works once it is acknowledged.
      if (e instanceof ChatApiError && e.status === 403 && e.code === 'notice_required') {
        this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: null, confirmClicked: !!c.confirmClicked });
        this.dispatch({ type: 'noticeRequired', pendingId: c.pendingId });
        return;
      }
      // CHAT-06/b: the daily report limit. No token was sent and the card stays as it was.
      const refusal = limitRefusal(e);
      if (refusal && refusal.kind === 'purchase') {
        this.holdPurchases(refusal.resetAt);
        return this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: purchaseRefusedText(refusal.resetAt), confirmClicked: !!c.confirmClicked });
      }
      if (e instanceof ChatApiError && e.status === 409 && e.code === 'confirmation_closed') {
        return this.settleCard(c, action, epoch, e.cardStatus || 'redeeming');
      }
      if (e instanceof ChatApiError && e.status === 409) {
        return this.cardFailed(c, action, { message: 'Ask Bizminer is still answering your last question. Give it a moment, then try again.' }, true);
      }
      if (e instanceof ChatApiError && e.status === 404) {
        return this.cardFailed(c, action, { message: 'That offer is no longer available. Ask again to get a new one.' }, true);
      }
      // Dropped connection, 5xx, anything else: what happened is unknown here.
      return this.settleCard(c, action, epoch);
    },
    // The card goes back to pending with a message and a button that works.
    // `unsent`: the service refused before it claimed anything, so this click does not count as one.
    async cardFailed(c, action, err, unsent = false) {
      if (err && err.code === 'reauth_required') {
        try {
          await openSession(); // renew the cookie so pressing the button again can succeed
        } catch (e) {
          /* the next click will show the real error */
        }
      }
      this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: purchaseErrorText(err, action, c.action), ...(unsent ? { confirmClicked: !!c.confirmClicked } : {}) });
    },
    // Re-read the conversation and let the server's card state win. If it cannot
    // be read: a closed card falls back to the status the 409 carried; otherwise
    // the card is clickable again with a message that does not claim "nothing was charged".
    async settleCard(c, action, epoch, serverStatus) {
      await this.reloadConversation(epoch);
      if (epoch !== this.epoch) return;
      const now = this.findCard(c.pendingId);
      if (!now) return;
      const waiting = now.status === 'buying' || now.status === 'declining';
      if (serverStatus) {
        if (waiting) {
          const p = confirmCardPatch(serverStatus, c.action);
          this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: p.status, charged: p.charged, message: p.message });
        }
        return;
      }
      if (waiting || now.status === 'pending') {
        this.dispatch({ type: 'confirmState', pendingId: c.pendingId, status: 'pending', error: purchaseErrorText(null, action, c.action) });
      }
    },
    // `background` (the settle loop's poll, D3): a turn can start while the request
    // is out, so the result may only patch cards, never replace the messages.
    async reloadConversation(epoch, { poll = true, background = false } = {}) {
      const id = this.chat.conversationId;
      if (!id) return false;
      const turns = this.turnSeq;
      try {
        const conv = await withReauth(() => getConversation(id, this.abortCtl.signal));
        if (epoch !== this.epoch) return false;
        const messages = messagesFromHistory(conv.messages);
        this.takeShare(conv);
        if (background) this.dispatch({ type: 'poll', conversationId: conv.id, messages, patchOnly: this.working || turns !== this.turnSeq });
        else this.dispatch({ type: 'history', conversationId: conv.id, messages });
        if (poll) this.settleLoop(epoch); // no-op when settled, or when the loop is already running
        return true;
      } catch (e) {
        if (epoch === this.epoch && isDenied(e)) this.$store.dispatch('chat/deny');
        return false;
      }
    },
    // F1 / F2 / P3: while an answer is `streaming` or a purchase is `redeeming` on
    // the server, re-read the conversation every 3 s for up to 60 s until it
    // settles. Then an unfinished answer becomes an interrupted one with Retry, and
    // a purchase still redeeming is polled every 15 s for up to 5 more minutes.
    // After that the card says "taking longer than usual" with a Check again button.
    // D3: the composer is disabled for the fast 60 s only (settleLocked). In the slow
    // phase the user can send again; the card keeps polling and each result only
    // patches the card while that turn streams.
    async settleLoop(epoch) {
      if (this.settling || epoch !== this.epoch || !needsSettle(this.chat.messages)) return;
      const signal = this.abortCtl.signal;
      this.settling = true;
      try {
        let attempt = 0;
        for (;;) {
          const step = settleStep({ messages: this.chat.messages, attempt });
          this.settleLocked = settleLocksComposer({ messages: this.chat.messages, attempt });
          if (step.action === 'stop') return;
          if (step.action === 'giveUp') {
            this.dispatch({ type: 'settleTimeout' });
            continue; // a redeeming card may remain: the next step is the slow phase
          }
          if (step.action === 'stalled') {
            this.dispatch({ type: 'settleStalled' });
            return;
          }
          await this.pause(step.delayMs, signal);
          if (epoch !== this.epoch) return;
          await this.reloadConversation(epoch, { poll: false, background: true });
          if (epoch !== this.epoch) return;
          attempt++;
        }
      } finally {
        if (epoch === this.epoch) {
          this.settling = false;
          this.settleLocked = false;
        }
      }
    },
    // P3: the "Check again" button on a stalled purchase: read the conversation once.
    // A purchase that is still redeeming goes back to "taking longer than usual".
    async recheck() {
      if (this.rechecking || this.busy) return;
      const epoch = this.epoch;
      this.rechecking = true;
      try {
        await this.reloadConversation(epoch, { poll: false });
        if (epoch !== this.epoch) return;
        this.dispatch({ type: 'settleStalled' });
        this.settleLoop(epoch); // only an answer that is still running keeps polling
      } finally {
        this.rechecking = false;
      }
    },
    pause(ms, signal) {
      return new Promise((resolve) => {
        const done = () => {
          clearTimeout(t);
          signal.removeEventListener('abort', done);
          resolve();
        };
        const t = setTimeout(done, ms);
        signal.addEventListener('abort', done);
      });
    },
    findCard(pendingId) {
      for (const m of this.chat.messages) {
        const c = (m.confirms || []).find((x) => x.pendingId === pendingId);
        if (c) return c;
      }
      return null;
    },
    // A My Data / Company Report action card (not a purchase); its words are in chatCore CARD_ACTIONS.
    isAction(c) {
      return isActionCard(c);
    },
    actionCopy(c) {
      return cardCopy(c);
    },
    confirmHeadline(c) {
      if (isActionCard(c)) return actionHeadline(c);
      return { owned: 'Added to your profiles and reports', already_owned: 'Already in your profiles and reports', not_covered: 'Not included in your plan', declined: 'Not purchased', expired: 'Offer expired', rejected: 'Offer expired', redeeming: 'Purchase in progress', stalled: 'Purchase in progress' }[c.status] || 'Confirm purchase';
    },
    chargeLine(c) {
      return chargeLine(c);
    },
    // The server's sub-user note ("This uses your team's subscription reports.").
    subNote(c) {
      return subUserNoteLine(c, this.subUser);
    },
    canOfferNewOffer(c) {
      return canOfferNewOffer(c);
    },
    expiryNote(c) {
      const t = c.expiresAt ? new Date(c.expiresAt) : null;
      if (!t || isNaN(t.getTime())) return 'This offer stays open for 15 minutes.';
      if (t.getTime() <= Date.now()) return isActionCard(c) ? cardCopy(c).expiredNote : 'This offer has timed out. Confirming shows you a fresh one.';
      return 'This offer stays open until ' + t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) + '.';
    },

    // --- share with support (CHAT-08) ------------------------------------------------
    clearShare() {
      this.sharedAt = null;
      this.shareNote = '';
    },
    // GET /conversations/:id says whether it is shared (and, while shared, what that means).
    takeShare(conv) {
      const s = normalizeShare(conv);
      this.sharedAt = s ? s.sharedAt : null;
      this.shareNote = s ? s.shareNote : '';
      // Tell the page, so its badge and Share / Stop sharing button follow the open conversation even when
      // a search hides the row.
      this.$emit('share-state', shareStateEvent(conv && conv.id != null ? conv.id : this.chat.conversationId, conv));
    },
    onShared({ id, sharedAt, shareNote }) {
      if (id !== this.chat.conversationId) return;
      this.sharedAt = sharedAt;
      this.shareNote = shareNote || this.shareNote;
    },
    onUnshared({ id }) {
      if (id === this.chat.conversationId) this.clearShare();
    },
    // The conversation is gone (404): start clean, like a deleted one.
    onShareMissing(id) {
      if (id !== this.chat.conversationId) return;
      this.resetConversation();
      this.notice = 'That conversation is no longer available.';
    },
    onShareAccess(e) {
      if (isDenied(e)) return this.$store.dispatch('chat/deny');
      if (isSubscriptionRequired(e)) return this.becomeTeaser(e);
    },
    // The cart link of the report-limit card: the service's cartUrl, only when it points at this app or bizminer.com.
    cartLink(p) {
      return p && p.cartUrl ? safeAppLink(p.cartUrl, window.location.origin) : null;
    },
    openCart(e, p) {
      this.openLink(e, this.cartLink(p));
      if (this.isPhone && !this.embedded && this.cartLink(p) && !this.cartLink(p).external) this.close();
    },

    // --- rendering helpers --------------------------------------------------
    render(text) {
      return renderMarkdownLite(text);
    },
    cite(c) {
      return citationLabel(c);
    },
    link(c) {
      return safeAppLink(c.appUrl, window.location.origin);
    },
    openCitation(e, c) {
      this.openLink(e, this.link(c));
    },
    // Same-origin links navigate through the router; external ones open as plain links.
    openLink(e, l) {
      if (l && !l.external) {
        e.preventDefault();
        this.$router.push(l.href).catch(() => {});
      }
    },
    // The buy link of a buy_in_app card: the service's buyUrl, only when it points at this app or bizminer.com.
    buyLink(n) {
      return n && n.buyUrl ? safeAppLink(n.buyUrl, window.location.origin) : null;
    },
    contact(n) {
      return contactSalesLink(n.contactUrl, window.location.origin);
    },
    hasBody(m) {
      return !!(m.text || m.ownedFree || m.citations.length || (m.charts && m.charts.length) || (m.prospects && m.prospects.length) || m.confirms.length || (m.notCovered && m.notCovered.length) || m.purchaseLimit || m.notice || (m.forms && m.forms.length) || m.error);
    },
    onScroll() {
      const el = this.$refs.msgs;
      if (el) this.pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },
    scrollDown(force) {
      this.$nextTick(() => {
        const el = this.$refs.msgs;
        if (el && (force || this.pinned)) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style lang="scss">
$bzc-navy: #003666;
$bzc-cyan: #0cbcf2;
$bzc-width: 400px;
$bzc-bar: 62px;
$bzc-phone: 600px;

.bzc-panel {
  position: fixed;
  top: $bzc-bar;
  right: 0;
  bottom: 0;
  width: $bzc-width;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-left: 1px solid #d7dde5;
  box-shadow: -4px 0 14px rgba(0, 30, 60, 0.1);
  font-family: Roboto, Arial, sans-serif;
  color: #22303f;
  text-align: left;

  button { font: inherit; cursor: pointer; }
  button:disabled { cursor: default; }
  p { margin: 0; }
}

// Page stays usable beside the panel: the layout wrapper (sidebar + main
// panel) narrows by the panel's width instead of sitting underneath it.
@media (min-width: 901px) {
  body.bzc-open .wrapper { width: calc(100% - #{$bzc-width}); }
}

// Narrow: the panel takes the whole width below the top bar.
@media (max-width: 900px) {
  .bzc-panel { left: 0; width: auto; border-left: 0; }
  body.bzc-open { overflow: hidden; }
}

// The floating button (phones only; the top-bar button does this job on bigger screens).
.bzc-fab { display: none; }
@media (max-width: $bzc-phone) {
  .bzc-fab {
    position: fixed; z-index: 999; display: inline-flex; align-items: center; gap: 9px;
    right: calc(16px + env(safe-area-inset-right)); bottom: calc(16px + env(safe-area-inset-bottom));
    height: 52px; padding: 0 18px 0 16px; border: 0; border-radius: 26px; background: $bzc-navy; color: #fff;
    font: 500 15px/1 Inter, Roboto, sans-serif; white-space: nowrap; cursor: pointer;
    box-shadow: 0 6px 18px rgba(0, 30, 60, 0.32), 0 1px 3px rgba(0, 30, 60, 0.2);
    -webkit-tap-highlight-color: transparent;
    &:active { background: #002a50; }
    &:focus-visible { outline: 3px solid $bzc-cyan; outline-offset: 2px; }
    svg { flex: 0 0 auto; }
  }
  .bzc-fab-lock { margin-left: -3px; opacity: 0.85; }
  .bzc-fab-beta { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; padding: 3px 7px; border-radius: 999px; background: $bzc-cyan; color: #002244; }
  // The Help Scout beacon lives bottom-right too: lift the button above it
  // (keyed on #beacon-container via body.bzc-beacon-on, not the beacon's inner frame classes).
  body.bzc-beacon-on .bzc-fab { bottom: calc(96px + env(safe-area-inset-bottom)); }
  // The open sheet is full screen: the beacon would overlay it, so hide it.
  body.bzc-phone-open #beacon-container { display: none !important; }
}

// Help Scout beacon: keep it clear of the open panel (fallback for the
// Beacon('config') call in the component).
@media (min-width: 901px) {
  body.bzc-open #beacon-container .BeaconFabButtonFrame,
  body.bzc-open #beacon-container .BeaconContainer {
    right: $bzc-width + 20px !important;
  }
}
@media (max-width: 900px) {
  body.bzc-open #beacon-container { display: none !important; }
}

.bzc-sr { position: absolute; left: -9999px; }
.bzc-spin { animation: bzc-spin 0.9s linear infinite; transform-origin: 50% 50%; }
@keyframes bzc-spin { to { transform: rotate(360deg); } }

.bzc-head {
  flex: 0 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 8px 0 16px;
  background: $bzc-navy;
  color: #fff;
}
// Two classes on purpose: vue-material's global `img:not(.md-image) { height: auto }` outranks a lone
// class and let the 98x92 source render at full size.
.bzc-head .bzc-head-logo { flex: 0 0 auto; display: block; height: 26px; width: auto; filter: brightness(0) invert(1); }
.bzc-head-title { flex: 1 1 auto; font-family: Inter, Roboto, sans-serif; font-size: 16px; font-weight: 600; }
.bzc-icon-btn {
  width: 36px; height: 36px; border: 0; border-radius: 18px; background: transparent; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.14); }
  &:disabled { opacity: 0.45; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: -2px; }
}

.bzc-context {
  flex: 0 0 auto; display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px 10px 16px;
  background: #eaf6fc; border-bottom: 1px solid #cfe8f4; font-size: 12px; line-height: 1.4; color: $bzc-navy;
}
.bzc-context-icon { flex: 0 0 auto; margin-top: 1px; }
.bzc-context-text { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.bzc-context-x {
  flex: 0 0 auto; width: 28px; height: 28px; margin: -4px -4px 0 0; border: 0; border-radius: 14px;
  background: transparent; color: $bzc-navy;
  &:hover { background: rgba(0, 54, 102, 0.1); }
}

.bzc-msgs {
  flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 18px 16px 12px;
  display: flex; flex-direction: column; gap: 16px; background: #f7f9fb; overflow-x: hidden;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #c9d3de; border-radius: 4px; }
}
.bzc-msg { display: flex; flex-direction: column; gap: 6px; }
.bzc-status { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #4f5a6b; }
.bzc-thinking { padding-left: 34px; }

.bzc-first { display: flex; flex-direction: column; gap: 14px; padding: 8px 4px; }
.bzc-first-title { font-family: Inter, Roboto, sans-serif; font-size: 20px; font-weight: 600; color: $bzc-navy; }
.bzc-first p { font-size: 14px; line-height: 1.5; color: #3c4656; }
.bzc-first-label { margin-top: 4px; font-size: 12px; font-weight: 700; letter-spacing: 0.06em; color: #5b6575; text-transform: uppercase; }
.bzc-starters { display: flex; flex-direction: column; gap: 8px; }
.bzc-chip {
  text-align: left; min-height: 36px; padding: 7px 12px; border-radius: 18px; border: 1px solid #c9dbe8;
  background: #fff; color: $bzc-navy; font-size: 13px;
  &:hover { background: #e3f6fd; border-color: $bzc-cyan; }
}
.bzc-chip-block { min-height: 44px; padding: 10px 14px; border-radius: 10px; font-size: 14px; line-height: 1.35; }

.bzc-user {
  align-self: flex-end; max-width: 86%; background: $bzc-navy; color: #fff; padding: 10px 14px;
  border-radius: 16px 16px 4px 16px; font-size: 14px; line-height: 1.45; white-space: pre-wrap; overflow-wrap: anywhere;
}

.bzc-steps { display: flex; flex-direction: column; gap: 5px; padding-left: 34px; }
.bzc-step { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #4f5a6b; svg { flex: 0 0 auto; } }

.bzc-assistant { display: flex; gap: 10px; align-items: flex-start; }
.bzc-avatar {
  flex: 0 0 24px; width: 24px; height: 24px; border-radius: 12px; background: $bzc-navy; margin-top: 2px;
  display: inline-flex; align-items: center; justify-content: center;
  img { height: 13px; width: auto; filter: brightness(0) invert(1); }
}
.bzc-body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 10px; font-size: 14px; line-height: 1.5; color: #22303f; }

.bzc-owned {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700;
  color: #1f5a24; background: #e4f2e5; padding: 3px 9px; border-radius: 999px;
}

.bzc-md {
  overflow-wrap: anywhere; min-width: 0; max-width: 100%;
  p { margin: 0 0 10px; &:last-child { margin-bottom: 0; } }
  ul, ol { margin: 0 0 10px; padding-left: 18px; li { margin-bottom: 4px; } }
  code { font-size: 12.5px; background: #eef2f6; padding: 1px 4px; border-radius: 3px; }
  // Wide tables scroll sideways inside their own box (never the page); the first
  // column stays put so the rows stay readable (CHAT-23/b).
  .bzc-table {
    max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; overscroll-behavior-x: contain;
    margin: 0 0 10px; border: 1px solid #dfe5ec; border-radius: 8px; background: #fff;
    table { width: 100%; min-width: max-content; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
    th, td { padding: 7px 10px; text-align: right; white-space: nowrap; background: #fff; border-top: 1px solid #edf0f4; }
    thead th { font-weight: 500; background: $bzc-navy; color: #fff; border-top: 0; }
    tbody tr:first-child > * { border-top: 0; }
    th:first-child, td:first-child {
      position: sticky; left: 0; z-index: 1; text-align: left; white-space: normal;
      min-width: 88px; max-width: 150px; overflow-wrap: break-word;
      box-shadow: 1px 0 0 #dfe5ec, 5px 0 6px -4px rgba(0, 30, 60, 0.22);
    }
    thead th:first-child { z-index: 2; }
    tbody th { color: $bzc-navy; font-weight: 500; }
  }
}

.bzc-cite {
  display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid #dfe5ec; border-radius: 8px;
  background: #fff; color: $bzc-navy; font-size: 12px; line-height: 1.35;
}
.bzc-cite-icon { flex: 0 0 auto; }
.bzc-cite-text { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
.bzc-cite-open { flex: 0 0 auto; font-weight: 700; color: #0a6fb3; text-decoration: none; &:hover { color: $bzc-navy; text-decoration: underline; } }

.bzc-confirm {
  border: 1px solid #b8cde0; border-radius: 10px; background: #fff; overflow: hidden; box-shadow: 0 1px 3px rgba(0, 30, 60, 0.08);
  &.is-expired,
  &.is-rejected,
  &.is-not_covered,
  &.is-redeeming { border-color: #d7dde5; }
}
.bzc-confirm-head {
  display: flex; align-items: center; gap: 8px; padding: 9px 14px; background: $bzc-navy; color: #fff; font-size: 13px; font-weight: 700;
}
.bzc-confirm.is-expired .bzc-confirm-head,
.bzc-confirm.is-rejected .bzc-confirm-head,
.bzc-confirm.is-not_covered .bzc-confirm-head,
.bzc-confirm.is-redeeming .bzc-confirm-head { background: #e9edf2; color: #3c4656; }
.bzc-confirm-body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 10px; }
.bzc-confirm-product { font-family: Inter, Roboto, sans-serif; font-size: 15px; font-weight: 700; color: #13213c; }
.bzc-confirm-dl {
  margin: 0; display: grid; grid-template-columns: 92px minmax(0, 1fr); gap: 5px 10px; font-size: 12.5px; line-height: 1.4;
  dt { color: #5b6575; }
  dd { margin: 0; color: #22303f; overflow-wrap: anywhere; }
}
.bzc-confirm-charge { font-size: 13.5px; font-weight: 700; line-height: 1.4; color: #13213c; }
.bzc-confirm-note { font-size: 12.5px; line-height: 1.45; color: #3c4656; }
.bzc-confirm-error { font-size: 12.5px; color: #a3261c; }
.bzc-subuser { font-size: 12.5px; line-height: 1.45; color: #6a4a00; background: #fff4d6; border-radius: 6px; padding: 7px 9px; }
.bzc-confirm-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.bzc-confirm-done { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #1f5a24; }
.bzc-fine { font-size: 11.5px; color: #6b7686; }
.bzc-fine-dark { font-size: 13px; color: #5b6575; }

.bzc-btn {
  min-height: 40px; padding: 0 16px; border-radius: 6px; border: 1px solid transparent; font-size: 13.5px; font-weight: 500;
  align-self: flex-start;
  &:disabled { opacity: 0.55; }
}
.bzc-btn-primary { background: $bzc-navy; color: #fff; &:hover:not(:disabled) { background: #002a50; } }
.bzc-btn-ghost { background: #fff; border-color: #c9d1db; color: #3c4656; padding: 0 14px; &:hover:not(:disabled) { background: #f1f3f6; } }
.bzc-btn-outline { background: #fff; border-color: $bzc-navy; color: $bzc-navy; }
.bzc-btn-danger { background: #a3261c; color: #fff; &:hover:not(:disabled) { background: #8a1f17; } }
// Links styled as buttons (Subscribe, Contact sales).
a.bzc-btn, .bzc-teaser-actions a.bzc-btn {
  display: inline-flex; align-items: center; justify-content: center; text-decoration: none;
  &:hover, &:focus { text-decoration: none; }
  &:focus-visible { outline: 2px solid $bzc-cyan; outline-offset: 2px; }
}
// !important: the app's stylesheet sets `a { color: #3b7ef1 !important }`, which turned these
// button labels blue (navy-on-navy for Subscribe).
a.bzc-btn-primary, a.bzc-btn-primary:hover, a.bzc-btn-primary:focus { color: #fff !important; }
a.bzc-btn-outline, a.bzc-btn-outline:hover, a.bzc-btn-outline:focus { color: $bzc-navy !important; }

// CHAT-08: the Shared badge on the header bar.
.bzc-shared {
  flex: 0 0 auto; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 999px;
  background: $bzc-cyan; color: #002244;
}
// CHAT-06/a: the daily message limit, above the shut composer.
.bzc-limit {
  display: flex; flex-direction: column; gap: 2px; padding: 9px 12px; border-radius: 8px; background: #fff4d6; border: 1px solid #f0dca0;
  font-size: 12.5px; line-height: 1.45; color: #6a4a00;
  b { font-size: 13px; }
}

// not_covered (CHAT-18/c): an info card, not an offer.
.bzc-stalled { display: flex; flex-direction: column; gap: 10px; }
.bzc-info { border: 1px solid #b8cde0; border-radius: 10px; background: #fff; overflow: hidden; }
.bzc-info-head { display: flex; align-items: center; gap: 8px; padding: 9px 14px; background: #eaf6fc; color: $bzc-navy; font-size: 13px; font-weight: 700; }
.bzc-info-body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 10px; }

// Locked teaser (CHAT-18/e).
.bzc-teaser {
  flex: 1 1 auto; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; align-items: flex-start; gap: 14px;
  padding: 28px 22px; background: #f7f9fb;
  p { font-size: 14px; line-height: 1.55; color: #3c4656; }
}
.bzc-teaser-lock {
  width: 52px; height: 52px; border-radius: 26px; background: #e3f6fd; color: $bzc-navy;
  display: inline-flex; align-items: center; justify-content: center;
}
.bzc-teaser-title { font-family: Inter, Roboto, sans-serif; font-size: 20px; font-weight: 600; line-height: 1.3; color: $bzc-navy; }
.bzc-teaser-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.bzc-teaser-retry { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; }

.bzc-summary-btn { gap: 8px; display: inline-flex; align-items: center; }
.bzc-notice { font-size: 12.5px; line-height: 1.4; color: #6a4a00; background: #fff4d6; border-radius: 6px; padding: 8px 10px; }

.bzc-error {
  display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; font-size: 13px; line-height: 1.45; color: #a3261c;
  background: #fdf0ee; border: 1px solid #f1c9c4; border-radius: 8px; padding: 8px 10px;
}
.bzc-link-btn { border: 0; background: transparent; padding: 0; font-weight: 700; color: #0a6fb3; text-decoration: underline; &:hover { color: $bzc-navy; } }
a.bzc-link-btn { cursor: pointer; text-decoration: underline; }

.bzc-composer { flex: 0 0 auto; border-top: 1px solid #e1e6ec; padding: 10px 12px; background: #fff; display: flex; flex-direction: column; gap: 6px; }
.bzc-composer-box {
  display: flex; align-items: flex-end; gap: 8px; border: 1px solid #b9c6d4; border-radius: 10px; padding: 6px 6px 6px 12px; background: #fff;
  &:focus-within { border-color: $bzc-cyan; box-shadow: 0 0 0 2px rgba(12, 188, 242, 0.2); }
}
.bzc-composer-label { flex: 1 1 auto; display: flex; margin: 0; }
.bzc-composer-label textarea {
  flex: 1 1 auto; border: 0; outline: none; resize: none; font: inherit; font-size: 14px; line-height: 1.4; color: #13213c;
  background: transparent; padding: 4px 0; min-width: 0;
}
.bzc-send {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 8px; border: 0; display: inline-flex; align-items: center; justify-content: center;
  background: $bzc-navy; color: #fff;
  &:disabled { background: #e3e8ee; color: #8a95a5; }
}
.bzc-composer-foot { display: flex; justify-content: space-between; gap: 10px; font-size: 11px; color: #6b7686; }
.bzc-left { flex: 0 0 auto; }

// --- Phones (CHAT-23). Last in the file on purpose: these override the base rules above.
//
// Phone (CHAT-23/a): a full-screen sheet over everything, including the top bar.
// 100dvh = the visible height as the browser toolbars come and go; the vh line
// is the fallback for browsers without dvh. While the keyboard is up the panel
// gets an inline top/height from window.visualViewport (see onViewport).
@media (max-width: $bzc-phone) {
  .bzc-panel {
    top: 0; bottom: auto; left: 0; right: 0; width: 100%; height: 100vh; height: 100dvh;
    z-index: 1040; border-left: 0; box-shadow: none;
    padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right);
    overscroll-behavior: contain;
  }
  .bzc-head { height: calc(56px + env(safe-area-inset-top)); padding: env(safe-area-inset-top) 6px 0 16px; }
  .bzc-head-title { font-size: 17px; }
  .bzc-icon-btn { width: 44px; height: 44px; border-radius: 22px; }
  .bzc-context { padding: 10px 8px 10px 16px; }
  .bzc-context-x { width: 44px; height: 44px; margin: -12px -6px -12px 0; border-radius: 22px; display: inline-flex; align-items: center; justify-content: center; }
  .bzc-msgs { padding: 16px 14px 12px; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }

  // Composer: pinned at the bottom of the sheet, clear of the home indicator.
  // 16px text, or iOS zooms the page when the field takes focus.
  .bzc-composer { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); }
  .bzc-panel.is-kb .bzc-composer { padding-bottom: 10px; }
  .bzc-composer-label textarea { font-size: 16px; }
  .bzc-send { width: 44px; height: 44px; border-radius: 10px; }

  // Tap targets of at least 44px (CHAT-23/c).
  .bzc-chip { min-height: 44px; }
  .bzc-link-btn { min-height: 44px; min-width: 44px; display: inline-flex; align-items: center; }
  .bzc-cite-open { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; min-width: 44px; margin: -10px -6px -10px 0; }
  .bzc-confirm-actions { flex-wrap: nowrap; }
  .bzc-confirm-actions .bzc-btn { flex: 1 1 0; align-self: stretch; }
  .bzc-teaser-actions .bzc-btn { flex: 1 1 140px; }
}
// CHAT-13: inside the /chat page the panel is a plain column that fills its pane. After
// the phone rules above on purpose (same specificity as the base rule plus one class).
.bzc-panel.is-embedded {
  position: relative; top: auto; right: auto; bottom: auto; left: auto; width: 100%; height: auto; flex: 1 1 0; min-height: 0;
  z-index: auto; border: 0; box-shadow: none; padding: 0; background: #fff; overscroll-behavior: auto;
}

// Any touch device, whatever its width.
@media (max-width: $bzc-phone), (pointer: coarse) {
  .bzc-btn { min-height: 44px; }
}
</style>
